import { create } from 'zustand';
import api, { apiError, apiFieldErrors, ensureCsrf } from '../lib/api';
import { joinSharedPrivate, leaveSharedPrivate, onEchoReconnect } from '../lib/echo';
import type {
  CartLine,
  Paginated,
  PaymentState,
  Proof,
  Quote,
  QuoteState,
  QuoteSummary,
  ShippingAddressInput,
} from '../types';

// Unregister handle for the reconnect-refetch subscription.
let offReconnect: (() => void) | null = null;

// Monotonic request token for the list fetch, mirroring CataloguePage: only the
// latest initiated request may commit. searchTerm is written at request START
// and rows at RESOLVE, so without this a slow response for an old term lands
// after a newer one and leaves the store describing rows it did not produce.
// Reachable in practice - the reconnect refetch fires on an arbitrary socket
// event, and the debounce cancels its timer but never an in-flight request.
let fetchQuotesSeq = 0;

interface QuoteStateChangedPayload {
  quote_id: number;
  state: QuoteState;
  previous_state: QuoteState;
  total: string;
}

interface ProofStatusChangedPayload {
  proof_id: number;
  quote_id: number;
  version: number;
  state: Proof['state'];
}

/**
 * A line with no `id` is being added; `product_id` is then required. Existing
 * lines omitted from `lines` are left untouched, so removal is explicit via
 * `removed_line_ids` and can never be an accident of omission.
 */
export interface AmendLineInput {
  id?: number;
  product_id?: number;
  variant_id?: number | null;
  qty: number;
  unit_price: number;
}

export interface AmendPayload {
  lines: AmendLineInput[];
  delivery?: number | null;
  notes?: string | null;
  removed_line_ids?: number[];
  /**
   * Free-form adjustments (discount/tax/fee). Omit to leave the set untouched;
   * send an array (including empty, to clear) to replace it wholesale.
   */
  adjustments?: { label: string; amount: number }[];
  /** Mandatory reason for the edit (>10 chars). Recorded in the edit trail. */
  remark?: string;
}

interface QuoteStoreState {
  quotes: Quote[];
  current: Quote | null;
  loading: boolean;
  /**
   * FETCH failures only. QuoteDetailPage and QuoteListPage render a full-page
   * ErrorState from this, which is right when there is no order to show.
   */
  error: string | null;
  /**
   * WRITE failures - a rejected send, proof, invoice, payment or cancel.
   * Deliberately separate from `error`: routing these through it replaced the
   * whole order with an error card, so a staffer who mistyped a PO reference
   * lost the page they were working on and had to navigate back to find out
   * what was wrong. Rendered as an inline alert beside the controls instead.
   */
  actionError: string | null;
  /** Clear the inline write error (on dismiss, or when starting a new action). */
  clearActionError: () => void;
  page: number;
  lastPage: number;
  subscribedCompany: number | null;
  summary: QuoteSummary | null;
  /**
   * Active orders-list search term, forwarded as `?q=`. Lives here rather than
   * in QuoteListPage because the reconnect refresh in subscribeCompany re-fetches
   * the list from store state: a component-local term would be invisible there,
   * so a socket drop would silently reset the user's filtered list to every order
   * while their term sat in the input.
   */
  searchTerm: string | undefined;
  /** Active list filter params (status, payment, sort, …), carried through reconnect refetches. */
  listParams: Record<string, string> | undefined;

  /** `term` filters by partial reference or exact id; omitted means no filter. */
  fetchQuotes: (page?: number, term?: string, params?: Record<string, string>) => Promise<void>;
  fetchSummary: () => Promise<void>;
  /** Accepts an opaque order reference (buyer URLs) or a numeric id. */
  fetchQuote: (idOrRef: string | number) => Promise<void>;
  createQuote: (
    companyId: number,
    lines: CartLine[],
    notes: string | null,
    neededBy?: string | null,
    idempotencyKey?: string | null,
    shippingAddress?: ShippingAddressInput | null,
    recipientConsent?: boolean,
  ) => Promise<Quote | null>;
  /**
   * Staff amendment of a DRAFT's lines, delivery and notes.
   *
   * Returns per-field errors rather than setting `actionError` like its
   * siblings: the editor is a form, so a rejected unit price belongs against
   * that input, not in a banner at the top of the page. An empty object means
   * success. Failures with no field to attach to come back under `_form`.
   */
  amend: (id: number, payload: AmendPayload) => Promise<Record<string, string>>;
  send: (id: number, proof?: { artwork_version_ref: string; notes?: string }) => Promise<void>;
  setApprovalOrder: (quoteId: number, order: 'price_first' | 'proof_first') => Promise<void>;
  accept: (id: number) => Promise<void>;
  procure: (id: number) => Promise<void>;
  /**
   * Stage (or replace) a DRAFT proof for one line, from a chosen artwork ref.
   * Per-line: proofs are prepared line by line, then sent together.
   */
  stageProof: (quoteId: number, lineId: number, artworkRef: string) => Promise<void>;
  /** Remove a line's open DRAFT proof (the staged-proof "Remove" control). */
  unstageProof: (quoteId: number, lineId: number) => Promise<void>;
  /**
   * Auto-stage the buyer's own designer artwork as DRAFT proofs for every
   * eligible line with none yet (staff review, then send). Idempotent; returns
   * the number newly staged so a caller can skip a refetch when nothing changed.
   */
  /** Flip every DRAFT proof on the order to SENT in one buyer email (staff). */
  sendProofs: (quoteId: number) => Promise<void>;
  /** Approve every SENT proof on the order. Resolves true on success. */
  approveAllProofs: (quoteId: number) => Promise<boolean>;
  decideProof: (
    proofId: number,
    decision: 'approve' | 'request_changes',
    notes: string | null,
    attachments?: string[],
  ) => Promise<void>;
  /** Staff re-send the buyer's proof-review email. Resolves true on success. */
  resendProof: (proofId: number) => Promise<boolean>;
  issueInvoice: (id: number, poRef: string, terms: string | null) => Promise<void>;
  /**
   * Staff manually reconcile a B2B invoice against real-world payment
   * evidence (bank transfer, cheque, cash) - there is no Stripe path for
   * B2B. Resolves true on success so a confirm modal (Void) only closes
   * when the write actually landed, mirroring cancelQuote.
   */
  reconcilePayment: (id: number, paymentState: PaymentState, note?: string, amountPaid?: number) => Promise<boolean>;
  /**
   * Staff confirm the goods are in hand, releasing the order to the floor.
   * Production no longer starts on the system's own say-so.
   */
  confirmStock: (id: number) => Promise<void>;
  /**
   * Staff confirm a shipped job delivered from the order page (a fallback when
   * the courier's delivery webhook is silent). Refreshes the quote so the order
   * closes once its last job is delivered.
   */
  markJobDelivered: (quoteId: number, jobId: number) => Promise<void>;
  /** Resolves true when payment was captured immediately (no redirect). */
  payNow: (id: number) => Promise<boolean>;
  /** Resolves true on success so the confirm modal only closes when the cancel actually landed. */
  cancelQuote: (id: number, reason?: string) => Promise<boolean>;
  /**
   * Clone a past order's cloneable lines into a fresh DRAFT, re-priced at
   * today's config (one-click reorder). Resolves the new quote on success so
   * the caller can navigate straight to it; null (with `actionError` set) on
   * failure - e.g. every line on the source was dropped/cancelled (422), or
   * the source isn't this buyer's to reorder (403).
   */
  reorderQuote: (sourceId: number) => Promise<Quote | null>;
  subscribeCompany: (companyId: number) => void;
  unsubscribeCompany: () => void;
}

export const useQuoteStore = create<QuoteStoreState>((set, get) => ({
  quotes: [],
  current: null,
  loading: false,
  error: null,
  actionError: null,
  page: 1,
  lastPage: 1,
  subscribedCompany: null,
  summary: null,
  searchTerm: undefined,
  listParams: undefined,

  clearActionError: () => set({ actionError: null }),

  fetchQuotes: async (page = 1, term, params) => {
    const seq = ++fetchQuotesSeq;
    set({ loading: true, error: null, searchTerm: term, listParams: params });
    try {
      // Omitted rather than sent empty: the API treats a blank q as no filter,
      // but keeping it out of the query string keeps the URL honest. `params`
      // (status, payment, sort, date/value ranges) is spread in the same way.
      const { data } = await api.get<Paginated<Quote>>('/quotes', {
        params: {
          page,
          ...(term ? { q: term } : {}),
          ...(params ?? {}),
        },
      });
      if (seq !== fetchQuotesSeq) return;
      set({
        quotes: data.data,
        page: data.meta?.current_page ?? page,
        lastPage: data.meta?.last_page ?? 1,
        loading: false,
      });
    } catch (err) {
      // A superseded request's failure is equally stale - surfacing it would
      // show an error for a search the user has already moved on from.
      if (seq !== fetchQuotesSeq) return;
      set({ loading: false, error: apiError(err) });
    }
  },

  fetchSummary: async () => {
    // Non-fatal for the dashboard: on failure the tiles simply stay empty.
    try {
      const { data } = await api.get<QuoteSummary>('/quotes/summary');
      set({ summary: data });
    } catch {
      set({ summary: null });
    }
  },

  fetchQuote: async (id) => {
    set({ loading: true, error: null });
    try {
      const { data } = await api.get<{ data: Quote }>(`/quotes/${id}`);
      set({ current: data.data, loading: false });
    } catch (err) {
      set({ loading: false, error: apiError(err) });
    }
  },

  createQuote: async (companyId, lines, notes, neededBy = null, idempotencyKey = null, shippingAddress = null, recipientConsent = false) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      const { data } = await api.post<{ data: Quote }>('/quotes', {
        company_id: companyId,
        notes,
        // Omit when unset so the API sees a true absence, not an empty string.
        needed_by: neededBy || null,
        // Same cart re-submitted (double click / retry) returns the original
        // quote server-side instead of creating a duplicate draft.
        idempotency_key: idempotencyKey,
        // Buyer's checkout ship-to; snapshotted server-side onto the quote.
        shipping_address: shippingAddress,
        // PDPA: buyer's acknowledgement they may share the recipient's details.
        recipient_consent: recipientConsent,
        line_items: lines.map((l) => ({
          product_id: l.product.id,
          variant_id: l.variant?.id ?? null,
          qty: l.qty,
          customization: l.customization,
        })),
      });
      return data.data;
    } catch (err) {
      set({ actionError: apiError(err) });
      return null;
    }
  },

  // Every write-path action sets `error` on failure instead of leaking an
  // unhandled promise rejection (the page awaits these in a try/finally with no
  // catch). On success the affected quote is refetched so state stays truthful
  // even if the Reverb broadcast is missed.
  amend: async (id, payload) => {
    try {
      await ensureCsrf();
      await api.patch(`/quotes/${id}/amend`, payload);
      await get().fetchQuote(id);
      return {};
    } catch (err) {
      const fields = apiFieldErrors(err);
      // A non-validation failure (network, 500, state guard) has no field to
      // attach to, so surface it under a reserved key the form shows at the
      // top of the editor rather than blanking the page.
      return Object.keys(fields).length > 0 ? fields : { _form: apiError(err) };
    }
  },

  send: async (id, proof) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      await api.post(`/quotes/${id}/send`, proof ?? {});
      await get().fetchQuote(id);
    } catch (err) {
      set({ actionError: apiError(err) });
    }
  },

  setApprovalOrder: async (quoteId, order) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      await api.patch(`/quotes/${quoteId}/approval-order`, { approval_order: order });
      await get().fetchQuote(quoteId);
    } catch (err) {
      set({ actionError: apiError(err) });
    }
  },

  accept: async (id) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      await api.post(`/quotes/${id}/accept`);
      await get().fetchQuote(id);
    } catch (err) {
      set({ actionError: apiError(err) });
    }
  },

  procure: async (id) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      await api.post(`/quotes/${id}/procure`);
      await get().fetchQuote(id);
    } catch (err) {
      set({ actionError: apiError(err) });
    }
  },

  stageProof: async (quoteId, lineId, artworkRef) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      await api.post(`/quotes/${quoteId}/lines/${lineId}/proofs`, {
        artwork_version_ref: artworkRef,
      });
      await get().fetchQuote(quoteId);
    } catch (err) {
      set({ actionError: apiError(err) });
    }
  },

  unstageProof: async (quoteId, lineId) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      await api.delete(`/quotes/${quoteId}/lines/${lineId}/proofs`);
      await get().fetchQuote(quoteId);
    } catch (err) {
      set({ actionError: apiError(err) });
    }
  },

  sendProofs: async (quoteId) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      await api.post(`/quotes/${quoteId}/proofs/send`);
      await get().fetchQuote(quoteId);
    } catch (err) {
      set({ actionError: apiError(err) });
    }
  },

  approveAllProofs: async (quoteId) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      await api.post(`/quotes/${quoteId}/proofs/approve-all`);
      await get().fetchQuote(quoteId);
      return true;
    } catch (err) {
      set({ actionError: apiError(err) });
      return false;
    }
  },

  decideProof: async (proofId, decision, notes, attachments) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      await api.post(`/proofs/${proofId}/decide`, { decision, notes, attachments });
      const current = get().current;
      if (current) await get().fetchQuote(current.id);
    } catch (err) {
      set({ actionError: apiError(err) });
    }
  },

  resendProof: async (proofId) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      await api.post(`/proofs/${proofId}/resend`);
      return true;
    } catch (err) {
      set({ actionError: apiError(err) });
      return false;
    }
  },

  issueInvoice: async (id, poRef, terms) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      await api.post(`/quotes/${id}/invoice`, { po_ref: poRef, terms });
      await get().fetchQuote(id);
    } catch (err) {
      set({ actionError: apiError(err) });
    }
  },

  reconcilePayment: async (id, paymentState, note, amountPaid) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      // H3/M21: PARTIAL must carry the collected amount so a later refund
      // credits only what was received.
      await api.post(`/quotes/${id}/payment`, {
        payment_state: paymentState,
        note,
        ...(amountPaid !== undefined ? { amount_paid: amountPaid } : {}),
      });
      await get().fetchQuote(id);
      return true;
    } catch (err) {
      set({ actionError: apiError(err) });
      return false;
    }
  },

  confirmStock: async (id) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      await api.post(`/quotes/${id}/confirm-stock`);
      await get().fetchQuote(id);
    } catch (err) {
      set({ actionError: apiError(err) });
    }
  },

  markJobDelivered: async (quoteId, jobId) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      await api.post(`/production-jobs/${jobId}/mark-delivered`);
      await get().fetchQuote(quoteId);
    } catch (err) {
      set({ actionError: apiError(err) });
    }
  },

  payNow: async (id) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      const { data } = await api.post<{ checkout_url: string; paid: boolean }>(`/quotes/${id}/pay`);
      if (data.paid) {
        // Fixture/dev: captured immediately - refresh to show production status
        // and report success so the caller can confirm (no redirect happens).
        await get().fetchQuote(id);
        return true;
      }
      // Stripe: redirect to hosted checkout.
      window.location.href = data.checkout_url;
    } catch (err) {
      // Payment provider / gateway failure: surface friendly copy so the pay
      // button never freezes on an unhandled rejection.
      set({ actionError: apiError(err) });
    }
    return false;
  },

  cancelQuote: async (id, reason) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      await api.post(`/quotes/${id}/cancel`, { reason });
      await get().fetchQuote(id);
      return true;
    } catch (err) {
      set({ actionError: apiError(err) });
      return false;
    }
  },

  reorderQuote: async (sourceId) => {
    set({ actionError: null });
    try {
      await ensureCsrf();
      const { data } = await api.post<{ data: Quote }>(`/quotes/${sourceId}/reorder`);
      return data.data;
    } catch (err) {
      set({ actionError: apiError(err) });
      return null;
    }
  },

  subscribeCompany: (companyId) => {
    if (get().subscribedCompany === companyId) return;
    get().unsubscribeCompany();

    // Reconcile after a socket reconnect: refresh the list and the open quote so
    // any state-changed/proof events missed while offline are picked up.
    offReconnect = onEchoReconnect(() => {
      // Carry the active search through the reconnect refetch, or a socket drop
      // silently resets the user's filtered list to every order while their term
      // sits in the box.
      void get().fetchQuotes(get().page, get().searchTerm, get().listParams);
      const current = get().current;
      if (current) void get().fetchQuote(current.id);
    });

    // Shared refcounted membership: other stores may listen on the same
    // company channel, so never tear it down directly (see lib/echo).
    joinSharedPrivate(`company.${companyId}`)
      .listen('.quote.state-changed', (e: QuoteStateChangedPayload) => {
        set((s) => ({
          quotes: s.quotes.map((q) =>
            q.id === e.quote_id ? { ...q, state: e.state, total: e.total } : q,
          ),
          current:
            s.current && s.current.id === e.quote_id
              ? { ...s.current, state: e.state, total: e.total }
              : s.current,
        }));
      })
      .listen('.proof.status-changed', (e: ProofStatusChangedPayload) => {
        // Refetch the affected quote so proof list + state stay authoritative.
        const current = get().current;
        if (current && current.id === e.quote_id) {
          void get().fetchQuote(e.quote_id);
        }
      });

    set({ subscribedCompany: companyId });
  },

  unsubscribeCompany: () => {
    const companyId = get().subscribedCompany;
    if (companyId !== null) {
      offReconnect?.();
      offReconnect = null;
      leaveSharedPrivate(`company.${companyId}`);
      set({ subscribedCompany: null });
    }
  },
}));
